import React, { useEffect, useState, useRef } from "react";
import { Search, MoreVertical, ChevronLeft, ChevronRight, Home, CircleUser, CircleUserRound  } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fetchUsers , updateUser } from "../../services/apiService";

const AccountManagement = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [dropdownOpen, setDropdownOpen] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  // modify setting changes
  const dropdownRefs = useRef({});
  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      let clickedOutside = true;

      Object.values(dropdownRefs.current).forEach(ref => {
        if (ref && ref.contains(event.target)) {
          clickedOutside = false;
        }
      });

      if (clickedOutside) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await fetchUsers();
        setAccounts(users.map(user => ({ 
          name: user.userId, 
          email: user.email, 
          id: user._id,
          company: user.companyInfo,
          type: user.userType,
          status: user.isAccountActive === "active"
          ? "Active"
          : user.isAccountActive === "suspended"
          ? "Suspended"
          : "Deleted",
        })));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    }, [activeTab]);

const filteredAccounts = accounts.filter(account => {
  const matchesSearch =
    account.name.trim().toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.email.trim().toLowerCase().includes(searchTerm.toLowerCase()) ||
    ((account.company || "").trim().toLowerCase().includes(searchTerm.toLowerCase()));
  
  if (activeTab === "all") return matchesSearch;
  if (activeTab === "active") return matchesSearch && account.status === "Active";
  if (activeTab === "suspended") return matchesSearch && account.status === "Suspended";
  if (activeTab === "deleted") return matchesSearch && account.status === "Deleted";
  
  return matchesSearch;
});
  

  const totalPages = Math.ceil(filteredAccounts.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const paginatedAccounts = filteredAccounts.slice(startIndex, startIndex + usersPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  const handleActionClick = async (email, action) => {
    setDropdownOpen(null);
  
    if (action === "deleted") {
      try {
        await updateUser(email, { isAccountActive: "deleted" });
        setAccounts(prevAccounts =>
          prevAccounts.map(account =>
            account.email === email ? { ...account, status: "Deleted" } : account
          )
        );
  
      } catch (error) {
        console.error("Error deleting user:", error);
      }
    }
  };
  
  

  const totalCount = accounts.length;
  const activeCount = accounts.filter(a => a.status === "Active").length;
  const suspendedCount = accounts.filter(a => a.status === "Suspended").length;
  const deletedCount = accounts.filter(a => a.status === "Deleted").length;

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header Section */}
      <div className="w-full p-4 flex justify-between items-center">
      <h1 className="text-xl hidden md:block">Account Management</h1>
          <button 
            onClick={() => navigate("/account-management/create")} 
            className={`w-full md:w-auto py-2 px-4 rounded-lg ${
              error?.includes("Access denied") 
                ? "bg-gray-600 cursor-not-allowed" 
                : "bg-blue-600 text-white"
            }`}
            disabled={error?.includes("Access denied")}
          >
            Create Account
          </button>
        </div>

      <div className="px-4">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
          type="text"
          placeholder="Search by User ID, email, or company"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full bg-gray-800 text-gray-300 pl-10 pr-4 py-2 rounded-lg"
        />
        </div>
        
        <div className="flex overflow-x-auto mb-4 space-x-8 text-sm border-b border-gray-700">
          <button
            onClick={() => setActiveTab("all")}
            className={`py-2 px-1 ${activeTab === "all" 
              ? "text-blue-400 border-b-2 border-blue-400" 
              : "text-gray-400"}`}
          >
            All ({totalCount})
          </button>
          <button
            onClick={() => setActiveTab("active")}
            className={`py-2 px-1 ${activeTab === "active" 
              ? "text-blue-400 border-b-2 border-blue-400" 
              : "text-gray-400"}`}
          >
            Active ({activeCount})
          </button>
          <button
            onClick={() => setActiveTab("suspended")}
            className={`py-2 px-1 ${activeTab === "suspended" 
              ? "text-blue-400 border-b-2 border-blue-400" 
              : "text-gray-400"}`}
          >
            Suspended ({suspendedCount})
          </button>
          <button
            onClick={() => setActiveTab("deleted")}
            className={`py-2 px-1 ${activeTab === "deleted" 
              ? "text-blue-400 border-b-2 border-blue-400" 
              : "text-gray-400"}`}
          >
            Deleted ({deletedCount})
          </button>
        </div>
      </div>

      <div className="hidden md:grid md:grid-cols-5 text-gray-400 text-sm px-4 py-2 border-b border-gray-800">
        <div>Name</div>
        <div>Email Address</div>
        <div>Company</div>
        <div>Type</div>
        <div className="flex justify-between">
          <span>Status</span>
          <span>Actions</span>
        </div>
      </div>

      <div className="px-4 pb-32">
        {loading ? (
          <p className="text-center text-gray-400 py-8">Loading accounts...</p>
        ) : error ? (
          <p className="text-center text-red-500 py-8">{error}</p>
        ) : paginatedAccounts.length === 0 ? (
          <p className="text-center text-gray-400 py-8">No accounts found.</p>
        ) : (
          <div className="divide-y divide-gray-800">
            {paginatedAccounts.map((account) => (
              <div key={account.id} className="py-4">
                 <div className="md:hidden">
              <div className="flex justify-between items-center cursor-pointer"
                  onClick={() => navigate(`/account-management/information/${account.email}`)}>
                <div>
                  <div className="text-white font-medium">{account.name}</div>
                  <div className="text-gray-400 text-sm">{account.email}</div>
                   </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();              
                        navigate(`/account-management/information/${account.email}`);
                      }}
                      className="p-1 rounded-full hover:bg-gray-700 transition-colors"
                    >
                      <CircleUserRound className="h-5 w-5 text-white-400" />
                    </button>
                  </div>
                </div>
                
                <div className="hidden md:grid md:grid-cols-5 md:items-center">
                   <div className="text-white cursor-pointer" onClick={() => navigate(`/account-management/information/${account.email}`)}>
                    {account.name}
                  </div>
                  <div className="text-gray-400 cursor-pointer" onClick={() => navigate(`/account-management/information/${account.email}`)}>
                    {account.email}
                  </div>
                  <div className="text-gray-300">{account.company}</div>
                  <div>
                    <span className="bg-gray-800 text-gray-300 text-xs px-2 py-1 rounded">
                      {account.type}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded ${
                      account.status === 'Active' ? 'bg-green-900 text-green-300' : 
                      account.status === 'Suspended' ? 'bg-yellow-900 text-yellow-300' :
                      'bg-red-900 text-red-300'
                    }`}>
                      {account.status}
                    </span>
                    
                   <div className="relative" ref={el => (dropdownRefs.current[account.id] = el)}>
                    <button onClick={(e) => { e.stopPropagation();
                      setDropdownOpen(dropdownOpen === account.id ? null : account.id);
                      }}
                    >
                        <MoreVertical className="h-5 w-5 text-gray-400" />
                      </button>
                      
                      {dropdownOpen === account.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg z-10 py-1 border border-gray-700">
                          {/* <button 
                            className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                            onClick={() => handleActionClick(account.id, 'suspend')}
                          >
                            Suspend / Active
                          </button> */}
                          <button 
                            className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDropdownOpen(null);
                                navigate(`/account-management/information/${account.email}`);
                              }}
                            >
                            Modify Settings
                          </button>
                          {/* <button 
                            className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700"
                            onClick={() => handleActionClick(account.email, 'deleted')}
                          >
                            Delete
                          </button> */}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-gray-800/95 backdrop-blur-sm">
        <div className=" mx-auto p-0">
          <div className="flex items-center justify-center py-2 space-x-4">
            <button onClick={prevPage} disabled={currentPage === 1} 
              className={`border border-gray-700 rounded-md p-2 ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-700"}`}>
              <ChevronLeft className="h-5 w-5 text-gray-400" />
            </button>
            
            <div className="flex space-x-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      currentPage === pageNum 
                        ? "bg-blue-900 text-blue-300" 
                        : "text-gray-400 hover:bg-gray-700"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button onClick={nextPage} disabled={currentPage === totalPages || totalPages === 0}
              className={`border border-gray-700 rounded-md p-2 ${currentPage === totalPages || totalPages === 0 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-700"}`}>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
          </div>
          
          <div className="flex justify-end border-t border-gray-700/50 py-2 px-4 p-0">
              <button onClick={() => navigate("/management")} className="text-gray-400 flex flex-col items-center cursor-pointer">
              <Home className="h-6 w-6" />
              <span className="text-xs">Management</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountManagement;